import React, { useCallback, useEffect, useRef, useState } from 'react';
import './MaintenancePage.css';

interface Particle {
    id: number;
    x: number;
    y: number;
    size: number;
    speedX: number;
    speedY: number;
    opacity: number;
    hue: number;
}

const MaintenancePage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const logoRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const positionRef = useRef({ x: 50, y: 50 });
    const directionRef = useRef({ x: 1, y: 1 });
    const [hue, setHue] = useState(220);
    const currentHueRef = useRef(220);
    const animationFrameRef = useRef<number | null>(null);
    const particleAnimationRef = useRef<number | null>(null);
    const initializedRef = useRef(false);
    const particlesRef = useRef<Particle[]>([]);
    const mouseRef = useRef({ x: 0, y: 0 });

    const speed = 0.6;

    const logoDimensionsRef = useRef({ width: 0, height: 0 });

    const handleLogoClick = () => {
        window.open("https://x.com/loragonnft", "_blank", "noopener,noreferrer");
    };

    // Initialize particles
    const initParticles = useCallback(() => {
        if (!containerRef.current) return;
        const { clientWidth, clientHeight } = containerRef.current;
        const particles: Particle[] = [];
        const particleCount = Math.min(80, Math.floor((clientWidth * clientHeight) / 15000));

        for (let i = 0; i < particleCount; i++) {
            particles.push({
                id: i,
                x: Math.random() * clientWidth,
                y: Math.random() * clientHeight,
                size: Math.random() * 3 + 1,
                speedX: (Math.random() - 0.5) * 0.5,
                speedY: (Math.random() - 0.5) * 0.5,
                opacity: Math.random() * 0.5 + 0.2,
                hue: Math.random() * 60 + 200, // Blue to purple range
            });
        }
        particlesRef.current = particles;
    }, []);

    // Animate particles
    const animateParticles = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) {
            particleAnimationRef.current = requestAnimationFrame(animateParticles);
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { clientWidth, clientHeight } = container;
        canvas.width = clientWidth;
        canvas.height = clientHeight;

        ctx.clearRect(0, 0, clientWidth, clientHeight);

        // Draw gradient background
        const gradient = ctx.createRadialGradient(
            clientWidth / 2, clientHeight / 2, 0,
            clientWidth / 2, clientHeight / 2, Math.max(clientWidth, clientHeight) / 1.5
        );
        gradient.addColorStop(0, `hsla(${currentHueRef.current}, 50%, 8%, 1)`);
        gradient.addColorStop(1, `hsla(${currentHueRef.current + 40}, 30%, 3%, 1)`);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, clientWidth, clientHeight);

        // Update and draw particles
        particlesRef.current.forEach(particle => {
            // Mouse interaction
            const dx = mouseRef.current.x - particle.x;
            const dy = mouseRef.current.y - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < 150) {
                const force = (150 - distance) / 150;
                particle.speedX -= (dx / distance) * force * 0.02;
                particle.speedY -= (dy / distance) * force * 0.02;
            }

            // Update position
            particle.x += particle.speedX;
            particle.y += particle.speedY;

            // Damping
            particle.speedX *= 0.99;
            particle.speedY *= 0.99;

            // Wrap around edges
            if (particle.x < 0) particle.x = clientWidth;
            if (particle.x > clientWidth) particle.x = 0;
            if (particle.y < 0) particle.y = clientHeight;
            if (particle.y > clientHeight) particle.y = 0;

            // Draw particle
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${particle.hue}, 70%, 60%, ${particle.opacity})`;
            ctx.fill();

            // Draw connections
            particlesRef.current.forEach(other => {
                if (particle.id >= other.id) return;
                const dx = particle.x - other.x;
                const dy = particle.y - other.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 120) {
                    ctx.beginPath();
                    ctx.moveTo(particle.x, particle.y);
                    ctx.lineTo(other.x, other.y);
                    ctx.strokeStyle = `hsla(${(particle.hue + other.hue) / 2}, 60%, 50%, ${0.15 * (1 - dist / 120)})`;
                    ctx.lineWidth = 0.5;
                    ctx.stroke();
                }
            });
        });

        particleAnimationRef.current = requestAnimationFrame(animateParticles);
    }, []);

    // Track mouse position
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            mouseRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Initialize particles on mount
    useEffect(() => {
        initParticles();
        particleAnimationRef.current = requestAnimationFrame(animateParticles);

        return () => {
            if (particleAnimationRef.current) {
                cancelAnimationFrame(particleAnimationRef.current);
            }
        };
    }, [initParticles, animateParticles]);

    // Measure logo
    useEffect(() => {
        const measureLogo = () => {
            if (logoRef.current && containerRef.current) {
                const rect = logoRef.current.getBoundingClientRect();
                logoDimensionsRef.current = {
                    width: rect.width,
                    height: rect.height
                };
            }
        };

        measureLogo();
        const timer = setTimeout(measureLogo, 200);
        return () => clearTimeout(timer);
    }, []);

    // Initialize position
    useEffect(() => {
        const initTimeout = setTimeout(() => {
            if (!initializedRef.current && containerRef.current && logoRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const containerHeight = containerRef.current.clientHeight;

                if (logoDimensionsRef.current.width === 0) {
                    const rect = logoRef.current.getBoundingClientRect();
                    logoDimensionsRef.current = {
                        width: rect.width,
                        height: rect.height
                    };
                }

                const logoWidth = logoDimensionsRef.current.width;
                const logoHeight = logoDimensionsRef.current.height;

                positionRef.current = {
                    x: Math.random() * (containerWidth - logoWidth),
                    y: Math.random() * (containerHeight - logoHeight)
                };

                initializedRef.current = true;
                logoRef.current.style.left = `${positionRef.current.x}px`;
                logoRef.current.style.top = `${positionRef.current.y}px`;
            }
        }, 100);

        return () => clearTimeout(initTimeout);
    }, []);

    // Bouncing animation
    useEffect(() => {
        const getNextHue = () => {
            const jump = Math.floor(Math.random() * 60) + 40; // 40-100 degree jump
            return (currentHueRef.current + jump) % 360;
        };

        const updateHue = () => {
            const newHue = getNextHue();
            currentHueRef.current = newHue;
            setHue(newHue);
        };

        const animate = () => {
            if (!containerRef.current || !logoRef.current) {
                animationFrameRef.current = requestAnimationFrame(animate);
                return;
            }

            const containerWidth = containerRef.current.clientWidth;
            const containerHeight = containerRef.current.clientHeight;
            const logoWidth = logoDimensionsRef.current.width;
            const logoHeight = logoDimensionsRef.current.height;

            let newX = positionRef.current.x + speed * directionRef.current.x;
            let newY = positionRef.current.y + speed * directionRef.current.y;
            let colorChanged = false;

            const rightEdgeThreshold = containerWidth - logoWidth;
            if (newX <= 0) {
                directionRef.current.x = Math.abs(directionRef.current.x);
                newX = 0;
                if (!colorChanged) { updateHue(); colorChanged = true; }
            } else if (newX >= rightEdgeThreshold) {
                directionRef.current.x = -Math.abs(directionRef.current.x);
                newX = rightEdgeThreshold;
                if (!colorChanged) { updateHue(); colorChanged = true; }
            }

            const bottomEdgeThreshold = containerHeight - logoHeight;
            if (newY <= 0) {
                directionRef.current.y = Math.abs(directionRef.current.y);
                newY = 0;
                if (!colorChanged) { updateHue(); colorChanged = true; }
            } else if (newY >= bottomEdgeThreshold) {
                directionRef.current.y = -Math.abs(directionRef.current.y);
                newY = bottomEdgeThreshold;
                if (!colorChanged) { updateHue(); colorChanged = true; }
            }

            positionRef.current = { x: newX, y: newY };
            logoRef.current.style.left = `${newX}px`;
            logoRef.current.style.top = `${newY}px`;

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            initParticles();
            if (containerRef.current && logoRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const containerHeight = containerRef.current.clientHeight;
                const logoWidth = logoRef.current.clientWidth;
                const logoHeight = logoRef.current.clientHeight;

                let newX = positionRef.current.x;
                let newY = positionRef.current.y;

                if (newX + logoWidth > containerWidth) {
                    newX = containerWidth - logoWidth;
                }
                if (newY + logoHeight > containerHeight) {
                    newY = containerHeight - logoHeight;
                }

                positionRef.current = { x: newX, y: newY };
                logoRef.current.style.left = `${newX}px`;
                logoRef.current.style.top = `${newY}px`;
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [initParticles]);

    return (
        <div className="maintenance-container" ref={containerRef}>
            <canvas ref={canvasRef} className="particle-canvas" />

            {/* Floating orbs */}
            <div className="floating-orbs">
                <div className="orb orb-1" style={{ background: `hsla(${hue}, 70%, 50%, 0.15)` }} />
                <div className="orb orb-2" style={{ background: `hsla(${hue + 60}, 70%, 50%, 0.1)` }} />
                <div className="orb orb-3" style={{ background: `hsla(${hue + 120}, 70%, 50%, 0.1)` }} />
            </div>

            {/* Main bouncing card */}
            <div
                className="maintenance-card"
                ref={logoRef}
                style={{
                    '--card-hue': hue,
                } as React.CSSProperties}
                // onClick={handleLogoClick}
            >
                <div className="card-glow" />
                <div className="card-content">
                    <div className="icon-container">
                        <svg className="maintenance-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <h1 className="maintenance-title">System Maintenance</h1>
                    <p className="maintenance-subtitle">We're making things better</p>
                    <div className="progress-bar">
                        <div className="progress-fill" />
                    </div>
                    {/* <p className="maintenance-cta">
                        Follow <span className="highlight">@loragonnft</span> for updates
                    </p> */}
                </div>
                <div className="card-shine" />
            </div>

            {/* Corner decoration */}
            <div className="corner-decoration top-left" />
            <div className="corner-decoration top-right" />
            <div className="corner-decoration bottom-left" />
            <div className="corner-decoration bottom-right" />
        </div>
    );
};

export default MaintenancePage;
